/**
 * Bridges the in-memory marking model (MarkedVariable / PersonMeta) and persistence:
 *  - buildScalarSelections: flatten to the tokenizer's ScalarSelectionInput[]
 *  - hydrateFromRows: reconstruct marking state from saved TemplateVariable rows
 *  - buildDesiredRows + signatures: diff/merge saved rows (match by Name; insert new /
 *    update changed / delete removed) so ItemIds survive across re-saves.
 */

import type { ResolvedVariable, ScalarSelectionInput } from '@/lib/docx/tokenize';
import type {
  TemplateVariable,
  TemplateVariableInsertInput,
} from '@/modules/templates/types/templates.types';
import {
  COMPANY_SUB_LABELS,
  SUB_LABELS,
  SUB_TYPE,
  type CompanyMeta,
  type CompanySubKind,
  type DefaultSigner,
  type MarkedVariable,
  type PersonMeta,
  type ScalarType,
  type SubKind,
} from './types';

// ---------------------------------------------------------------------------
// Tokenizer input
// ---------------------------------------------------------------------------

/**
 * Flatten fields + person children into scalar selections for the tokenizer.
 *
 * Signature sub-fields are excluded: they are location anchors, not fill-in text, so we
 * must NOT inject a `{token}` or blank their span — the original signing-line text stays
 * intact in the document. Their anchor is persisted via SourceSpan (see buildDesiredRows).
 */
export function buildScalarSelections(variables: MarkedVariable[]): ScalarSelectionInput[] {
  return variables
    .filter((v) => v.subKind !== 'signature')
    .map((v) => ({
      name: v.name,
      selectedText: v.selectedText,
      contextBefore: v.contextBefore,
      contextAfter: v.contextAfter,
      paragraphIndex: v.paragraphIndex,
      occurrenceIndex: v.occurrenceIndex ?? null,
      occurrenceCount: v.occurrenceCount ?? null,
    }));
}

// ---------------------------------------------------------------------------
// Hydration (saved rows → marking state)
// ---------------------------------------------------------------------------

interface SourceSpanJson {
  paragraphIndex?: number | null;
  originalText?: string;
  contextBefore?: string;
  contextAfter?: string;
  /** Absent on rows written before the occurrence tiebreak existed; those resolve by context. */
  occurrenceIndex?: number | null;
  occurrenceCount?: number | null;
}

function parseSourceSpan(raw: string | undefined): SourceSpanJson {
  if (!raw) return {};
  try {
    return JSON.parse(raw) as SourceSpanJson;
  } catch {
    return {};
  }
}

/**
 * Both person and company children store their slot under the same `subKind` TypeMeta
 * key — the PARENT row's Type ('person' vs 'company') says which union to read it as.
 */
function parseSubKind(raw: string | undefined): SubKind | undefined {
  if (!raw) return undefined;
  try {
    const meta = JSON.parse(raw) as { subKind?: SubKind };
    return meta.subKind;
  } catch {
    return undefined;
  }
}

function parseCompanySubKind(raw: string | undefined): CompanySubKind | undefined {
  if (!raw) return undefined;
  try {
    const meta = JSON.parse(raw) as { subKind?: CompanySubKind };
    return meta.subKind;
  } catch {
    return undefined;
  }
}

/** `valueCopyFrom` (source field name) stored in a plain field's TypeMeta JSON. */
function parseValueCopyFrom(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  try {
    const meta = JSON.parse(raw) as { valueCopyFrom?: string };
    return meta.valueCopyFrom || undefined;
  } catch {
    return undefined;
  }
}

interface PersonTypeMetaJson {
  isSignatory?: boolean;
  signingOrder?: number | null;
  companyRef?: string | null;
  companyLabel?: string | null;
  defaultSigner?: DefaultSigner | null;
}

/** Parse the person PARENT row's TypeMeta into the signatory/company/default-signer meta. */
function parsePersonMeta(key: string, label: string, raw: string | undefined): PersonMeta {
  let m: PersonTypeMetaJson = {};
  if (raw) {
    try {
      m = JSON.parse(raw) as PersonTypeMetaJson;
    } catch {
      m = {};
    }
  }
  return {
    key,
    label,
    isSignatory: !!m.isSignatory,
    signingOrder: m.signingOrder ?? null,
    companyRef: m.companyRef ?? null,
    companyLabel: m.companyLabel ?? null,
    defaultSigner: m.defaultSigner ?? null,
  };
}

/** Currency code stored in a field's Format JSON (e.g. {"currency":"CHF"}). */
function parseCurrency(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  try {
    const f = JSON.parse(raw) as { currency?: string };
    return f.currency;
  } catch {
    return undefined;
  }
}

/**
 * Reconstruct { variables, persons, companies } from saved rows. PARENT rows (Type
 * "person" / "company") become PersonMeta / CompanyMeta; every other row becomes a
 * MarkedVariable, with children carrying personKey/subKind or companyKey/companySubKind
 * so the UI regroups them.
 */
export function hydrateFromRows(rows: TemplateVariable[]): {
  variables: MarkedVariable[];
  persons: Record<string, PersonMeta>;
  companies: Record<string, CompanyMeta>;
} {
  const ordered = [...rows].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const persons: Record<string, PersonMeta> = {};
  const companies: Record<string, CompanyMeta> = {};
  const personNameById = new Map<string, string>();
  const companyNameById = new Map<string, string>();

  for (const r of ordered) {
    if (r.type === 'person' && r.name) {
      persons[r.name] = parsePersonMeta(r.name, r.label ?? r.name, r.typeMeta);
      personNameById.set(r.id, r.name);
    } else if (r.type === 'company' && r.name) {
      companies[r.name] = { key: r.name, label: r.label ?? r.name };
      companyNameById.set(r.id, r.name);
    }
  }

  const variables: MarkedVariable[] = [];
  for (const r of ordered) {
    if (r.type === 'person' || r.type === 'company') continue; // parent, not a scalar field
    const span = parseSourceSpan(r.sourceSpan);
    const personKey = r.parentId ? personNameById.get(r.parentId) : undefined;
    const companyKey = r.parentId ? companyNameById.get(r.parentId) : undefined;
    const subKind = personKey ? parseSubKind(r.typeMeta) : undefined;
    const companySubKind = companyKey ? parseCompanySubKind(r.typeMeta) : undefined;
    variables.push({
      name: r.name ?? '',
      label: r.label ?? r.name ?? '',
      type: (r.type as ScalarType) ?? 'text',
      required: r.required ?? false,
      options: r.options ?? [],
      placeholder: r.placeholder || undefined,
      helpText: r.helpText || undefined,
      currencyCode: r.type === 'currency' ? parseCurrency(r.format) : undefined,
      valueCopyFrom: personKey || companyKey ? undefined : parseValueCopyFrom(r.typeMeta),
      selectedText: span.originalText ?? '',
      contextBefore: span.contextBefore ?? '',
      contextAfter: span.contextAfter ?? '',
      paragraphIndex: span.paragraphIndex ?? null,
      occurrenceIndex: span.occurrenceIndex ?? null,
      occurrenceCount: span.occurrenceCount ?? null,
      ...(personKey ? { personKey, subKind: subKind ?? 'name' } : {}),
      ...(companyKey ? { companyKey, companySubKind: companySubKind ?? 'name' } : {}),
    });
  }

  return { variables, persons, companies };
}

// ---------------------------------------------------------------------------
// Diff / merge (marking state → row sync plan)
// ---------------------------------------------------------------------------

export interface DesiredPersonRow {
  key: string;
  input: TemplateVariableInsertInput;
}
export interface DesiredCompanyRow {
  key: string;
  input: TemplateVariableInsertInput;
}
export interface DesiredFieldRow {
  name: string;
  input: TemplateVariableInsertInput;
}
export interface DesiredChildRow {
  name: string;
  /** The owning entity's key — a person key OR a company key (one shared namespace). */
  parentKey: string;
  /** ParentId is filled in at execution time from the (possibly newly-created) parent's ItemId. */
  input: Omit<TemplateVariableInsertInput, 'ParentId'>;
}

export interface DesiredRows {
  persons: DesiredPersonRow[];
  companies: DesiredCompanyRow[];
  fields: DesiredFieldRow[];
  children: DesiredChildRow[];
}

function sourceSpanJson(resolved: ResolvedVariable | undefined, v: MarkedVariable): string {
  const span = resolved?.sourceSpan ?? {
    docPart: 'document',
    paragraphIndex: v.paragraphIndex,
    originalText: v.selectedText,
    contextBefore: v.contextBefore,
    contextAfter: v.contextAfter,
    occurrenceIndex: v.occurrenceIndex ?? null,
    occurrenceCount: v.occurrenceCount ?? null,
  };
  return JSON.stringify(span);
}

/**
 * Build the desired row set from the marking state + tokenizer resolution. Parents are
 * emitted in first-appearance order; `Order` is the flat index across all variables so
 * hydration restores the same sequence.
 */
export function buildDesiredRows(
  templateId: string,
  variables: MarkedVariable[],
  persons: Record<string, PersonMeta>,
  companies: Record<string, CompanyMeta>,
  resolved: ResolvedVariable[]
): DesiredRows {
  const byName = new Map(resolved.map((r) => [r.name, r]));
  const out: DesiredRows = { persons: [], companies: [], fields: [], children: [] };
  const seenPersonKeys = new Set<string>();
  const seenCompanyKeys = new Set<string>();
  let order = 0;

  variables.forEach((v) => {
    if (v.personKey && v.subKind) {
      if (!seenPersonKeys.has(v.personKey)) {
        seenPersonKeys.add(v.personKey);
        const meta = persons[v.personKey];
        out.persons.push({
          key: v.personKey,
          input: {
            TemplateId: templateId,
            ParentId: null,
            Name: v.personKey,
            Label: meta?.label ?? v.personKey,
            Type: 'person',
            Required: false,
            TypeMeta: JSON.stringify({
              isSignatory: meta?.isSignatory ?? false,
              signingOrder: meta?.signingOrder ?? null,
              companyRef: meta?.companyRef ?? null,
              companyLabel: meta?.companyLabel ?? null,
              defaultSigner: meta?.defaultSigner ?? null,
            }),
            Order: order++,
          },
        });
      }
      out.children.push({
        name: v.name,
        parentKey: v.personKey,
        input: {
          TemplateId: templateId,
          Token: byName.get(v.name)?.token,
          Name: v.name,
          Label: v.label,
          Type: v.type,
          Required: v.required,
          Options: v.options?.length ? v.options : undefined,
          TypeMeta: JSON.stringify({ subKind: v.subKind }),
          SourceSpan: sourceSpanJson(byName.get(v.name), v),
          Order: order++,
        },
      });
    } else if (v.companyKey && v.companySubKind) {
      if (!seenCompanyKeys.has(v.companyKey)) {
        seenCompanyKeys.add(v.companyKey);
        const meta = companies[v.companyKey];
        out.companies.push({
          key: v.companyKey,
          input: {
            TemplateId: templateId,
            ParentId: null,
            Name: v.companyKey,
            Label: meta?.label ?? v.companyKey,
            Type: 'company',
            Required: false,
            TypeMeta: JSON.stringify({}),
            Order: order++,
          },
        });
      }
      out.children.push({
        name: v.name,
        parentKey: v.companyKey,
        input: {
          TemplateId: templateId,
          Token: byName.get(v.name)?.token,
          Name: v.name,
          Label: v.label,
          Type: v.type,
          Required: v.required,
          Options: v.options?.length ? v.options : undefined,
          TypeMeta: JSON.stringify({ subKind: v.companySubKind }),
          SourceSpan: sourceSpanJson(byName.get(v.name), v),
          Order: order++,
        },
      });
    } else {
      out.fields.push({
        name: v.name,
        input: {
          TemplateId: templateId,
          ParentId: null,
          Token: byName.get(v.name)?.token,
          Name: v.name,
          Label: v.label,
          Type: v.type,
          Required: v.required,
          Options: v.options?.length ? v.options : undefined,
          Placeholder: v.placeholder || undefined,
          HelpText: v.helpText || undefined,
          Format:
            v.type === 'currency' && v.currencyCode
              ? JSON.stringify({ currency: v.currencyCode })
              : undefined,
          TypeMeta: v.valueCopyFrom
            ? JSON.stringify({ valueCopyFrom: v.valueCopyFrom })
            : undefined,
          SourceSpan: sourceSpanJson(byName.get(v.name), v),
          Order: order++,
        },
      });
    }
  });

  return out;
}

/**
 * Map each row's Name to its persisted `Order`, replicating buildDesiredRows' numbering
 * (person parent emitted at its first child, then that child, then plain fields — one flat
 * counter across all). Person parents are keyed by their personKey. Used by the order-only
 * save path to write just the moved rows' Order without re-tokenizing.
 */
export function buildOrderMap(variables: MarkedVariable[]): Map<string, number> {
  const map = new Map<string, number>();
  const seenEntityKeys = new Set<string>();
  let order = 0;
  variables.forEach((v) => {
    const entityKey = v.personKey && v.subKind ? v.personKey : v.companyKey && v.companySubKind ? v.companyKey : null;
    if (entityKey) {
      if (!seenEntityKeys.has(entityKey)) {
        seenEntityKeys.add(entityKey);
        map.set(entityKey, order++);
      }
      map.set(v.name, order++);
    } else {
      map.set(v.name, order++);
    }
  });
  return map;
}

/** Person sub-field labels are derived from the person label; exported for reuse. */
export function childLabel(personLabel: string, sub: SubKind): string {
  return `${personLabel} — ${SUB_LABELS[sub]}`;
}

/** Company sub-field labels are derived from the company label. */
export function companyChildLabel(companyLabel: string, sub: CompanySubKind): string {
  return `${companyLabel} — ${COMPANY_SUB_LABELS[sub]}`;
}

export { SUB_TYPE };

/**
 * Stable signature of the persisted-relevant fields, so re-saving unchanged rows is a
 * no-op. Compares an existing row against a desired insert input (ParentId compared
 * separately by the executor, since it resolves at run time).
 */
export function rowSignature(
  row: Pick<
    TemplateVariableInsertInput,
    | 'Label'
    | 'Type'
    | 'Required'
    | 'Token'
    | 'Options'
    | 'Placeholder'
    | 'HelpText'
    | 'Format'
    | 'TypeMeta'
    | 'SourceSpan'
    | 'Order'
  >
): string {
  return JSON.stringify({
    Label: row.Label ?? '',
    Type: row.Type ?? '',
    Required: row.Required ?? false,
    Token: row.Token ?? '',
    Options: row.Options ?? [],
    Placeholder: row.Placeholder ?? '',
    HelpText: row.HelpText ?? '',
    Format: row.Format ?? '',
    TypeMeta: row.TypeMeta ?? '',
    SourceSpan: row.SourceSpan ?? '',
    Order: row.Order ?? 0,
  });
}

/** Signature for an existing (camelCase) saved row, matching rowSignature's shape. */
export function existingRowSignature(row: TemplateVariable): string {
  return JSON.stringify({
    Label: row.label ?? '',
    Type: row.type ?? '',
    Required: row.required ?? false,
    Token: row.token ?? '',
    Options: row.options ?? [],
    Placeholder: row.placeholder ?? '',
    HelpText: row.helpText ?? '',
    Format: row.format ?? '',
    TypeMeta: row.typeMeta ?? '',
    SourceSpan: row.sourceSpan ?? '',
    Order: row.order ?? 0,
  });
}

function variableFingerprint(v: MarkedVariable) {
  return {
    name: v.name,
    label: v.label,
    type: v.type,
    required: v.required,
    options: v.options ?? [],
    placeholder: v.placeholder ?? '',
    helpText: v.helpText ?? '',
    currencyCode: v.currencyCode ?? '',
    valueCopyFrom: v.valueCopyFrom ?? '',
    selectedText: v.selectedText,
    contextBefore: v.contextBefore,
    contextAfter: v.contextAfter,
    // Which occurrence was marked is part of a field's identity, not decoration. Re-marking
    // an identical table row changes nothing else about the field — omit this and the
    // "nothing changed since load" fast path would skip the save and keep the old span.
    // Legacy rows hydrate this as null on both sides, so they stay clean and skip as before.
    occurrenceIndex: v.occurrenceIndex ?? null,
    personKey: v.personKey ?? '',
    subKind: v.subKind ?? '',
    companyKey: v.companyKey ?? '',
    companySubKind: v.companySubKind ?? '',
  };
}

function companiesFingerprint(companies: Record<string, CompanyMeta>) {
  return Object.keys(companies)
    .sort()
    .map((k) => ({ key: k, label: companies[k].label }));
}

function personsFingerprint(persons: Record<string, PersonMeta>) {
  return Object.keys(persons)
    .sort()
    .map((k) => {
      const m = persons[k];
      return {
        key: k,
        label: m.label,
        isSignatory: m.isSignatory,
        signingOrder: m.signingOrder,
        companyRef: m.companyRef,
        companyLabel: m.companyLabel,
        defaultSigner: m.defaultSigner ?? null,
      };
    });
}

/**
 * Canonical signature of the whole marking state — used to detect whether anything
 * changed since load, so an unchanged "Save and Continue" can skip re-tokenization.
 * Order-SENSITIVE: variables are serialized in array order.
 */
export function markingSignature(
  variables: MarkedVariable[],
  persons: Record<string, PersonMeta>,
  companies: Record<string, CompanyMeta>
): string {
  return JSON.stringify({
    v: variables.map(variableFingerprint),
    p: personsFingerprint(persons),
    c: companiesFingerprint(companies),
  });
}

/**
 * Order-INSENSITIVE signature (variables sorted by name). When this matches the saved
 * snapshot but markingSignature does not, the only thing that changed is row order — so
 * save can skip tokenization/upload and just rewrite the Order field.
 */
export function contentSignature(
  variables: MarkedVariable[],
  persons: Record<string, PersonMeta>,
  companies: Record<string, CompanyMeta>
): string {
  const sorted = [...variables]
    .map(variableFingerprint)
    .sort((a, b) => a.name.localeCompare(b.name));
  return JSON.stringify({
    v: sorted,
    p: personsFingerprint(persons),
    c: companiesFingerprint(companies),
  });
}
